package com.isaias.fyattravel.web.auth

import androidx.compose.runtime.*
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlin.js.Json
import kotlin.js.json

/**
 * Firebase Auth Manager for Web
 * Manages authentication state and user session
 */
object AuthManager {
    private const val USER_KEY = "fyat_user"
    private const val TOKEN_KEY = "fyat_token"
    
    private val _currentUser = mutableStateOf<WebUser?>(null)
    val currentUser: State<WebUser?> = _currentUser
    
    private val _isAuthenticated = mutableStateOf(false)
    val isAuthenticated: State<Boolean> = _isAuthenticated
    
    private val _isLoading = mutableStateOf(true)
    val isLoading: State<Boolean> = _isLoading
    
    // Firebase Auth instance (initialized from JS)
    private var firebaseAuth: dynamic = null
    private var firebaseUI: dynamic = null
    
    /**
     * Initialize Firebase Auth
     * Firebase app is already initialized in index.html
     */
    fun initialize() {
        try {
            if (js("typeof firebase") == "undefined") {
                console.error("Firebase not loaded")
                _isLoading.value = false
                return
            }

            // Get auth instance - app already initialized in index.html
            firebaseAuth = js("firebase.auth()")

            if (firebaseAuth == null) {
                console.error("Firebase auth is null")
                _isLoading.value = false
                return
            }

            // Check for stored user
            loadUserFromStorage()

            // Listen to auth state changes
            firebaseAuth.onAuthStateChanged { user: dynamic ->
                if (user != null) {
                    val webUser = WebUser(
                        uid = user.uid as String,
                        email = user.email as? String,
                        displayName = user.displayName as? String,
                        photoURL = user.photoURL as? String,
                        phoneNumber = user.phoneNumber as? String,
                        isEmailVerified = user.emailVerified as? Boolean ?: false
                    )
                    _currentUser.value = webUser
                    _isAuthenticated.value = true
                    saveUserToStorage(webUser)

                    // Get and store token
                    user.getIdToken().then { token: dynamic ->
                        localStorage.setItem(TOKEN_KEY, token as String)
                    }
                } else {
                    _currentUser.value = null
                    _isAuthenticated.value = false
                    clearStorage()
                }
                _isLoading.value = false
            }
            
        } catch (e: dynamic) {
            console.error("Error initializing Firebase Auth:", e)
            _isLoading.value = false
        }
    }
    
    /**
     * Start Firebase Auth UI
     */
    fun startAuthUI(containerId: String, onSuccess: () -> Unit, onError: (String) -> Unit) {
        try {
            if (js("typeof firebaseui") == "undefined") {
                onError("Firebase UI not loaded")
                return
            }
            
            val uiConfig = json(
                "signInSuccessUrl" to "#/",
                "signInOptions" to arrayOf(
                    js("firebase.auth.GoogleAuthProvider.PROVIDER_ID")
                ),
                "callbacks" to json(
                    "signInSuccessWithAuthResult" to { authResult: dynamic, redirectUrl: dynamic ->
                        onSuccess()
                        false // Don't redirect automatically
                    },
                    "uiShown" to {
                        console.log("Auth UI shown")
                    }
                ),
                "credentialHelper" to js("firebaseui.auth.CredentialHelper.NONE")
            )
            
            // Create or get existing UI instance
            firebaseUI = js("firebaseui.auth.AuthUI.getInstance()") 
                ?: js("new firebaseui.auth.AuthUI")(firebaseAuth)
            
            firebaseUI.start("#$containerId", uiConfig)
            
        } catch (e: dynamic) {
            console.error("Error starting Firebase UI:", e)
            onError("Failed to start authentication: ${e.message}")
        }
    }
    
    /**
     * Sign out the current user
     */
    fun signOut(onComplete: () -> Unit = {}) {
        if (firebaseAuth != null) {
            firebaseAuth.signOut().then {
                _currentUser.value = null
                _isAuthenticated.value = false
                clearStorage()
                onComplete()
            }.catch { error: dynamic ->
                console.error("Error signing out:", error)
                onComplete()
            }
        } else {
            onComplete()
        }
    }
    
    /**
     * Get the current auth token
     */
    fun getToken(): String? {
        return localStorage.getItem(TOKEN_KEY)
    }
    
    /**
     * Refresh the auth token
     */
    fun refreshToken(onSuccess: (String) -> Unit, onError: (String) -> Unit) {
        val user = firebaseAuth?.currentUser
        if (user != null) {
            user.getIdToken(true).then { token: dynamic ->
                val tokenStr = token as String
                localStorage.setItem(TOKEN_KEY, tokenStr)
                onSuccess(tokenStr)
            }.catch { error: dynamic ->
                onError(error.message as String)
            }
        } else {
            onError("No user logged in")
        }
    }
    
    /**
     * Update user profile
     */
    fun updateProfile(displayName: String?, photoURL: String?, onComplete: (Boolean) -> Unit) {
        val user = firebaseAuth?.currentUser
        if (user != null) {
            val profile = json()
            if (displayName != null) profile["displayName"] = displayName
            if (photoURL != null) profile["photoURL"] = photoURL
            
            user.updateProfile(profile).then {
                // Update local state
                _currentUser.value = _currentUser.value?.copy(
                    displayName = displayName,
                    photoURL = photoURL
                )
                _currentUser.value?.let { saveUserToStorage(it) }
                onComplete(true)
            }.catch { error: dynamic ->
                console.error("Error updating profile:", error)
                onComplete(false)
            }
        } else {
            onComplete(false)
        }
    }
    
    /**
     * Send email verification
     */
    fun sendEmailVerification(onComplete: (Boolean) -> Unit) {
        val user = firebaseAuth?.currentUser
        if (user != null) {
            user.sendEmailVerification().then {
                onComplete(true)
            }.catch { error: dynamic ->
                console.error("Error sending verification:", error)
                onComplete(false)
            }
        } else {
            onComplete(false)
        }
    }
    
    private fun loadUserFromStorage() {
        try {
            val userJson = localStorage.getItem(USER_KEY)
            if (userJson != null) {
                val user = JSON.parse<WebUser>(userJson)
                _currentUser.value = user
                _isAuthenticated.value = true
            }
        } catch (e: dynamic) {
            console.error("Error loading user from storage:", e)
        }
    }
    
    private fun saveUserToStorage(user: WebUser) {
        try {
            localStorage.setItem(USER_KEY, JSON.stringify(user))
        } catch (e: dynamic) {
            console.error("Error saving user to storage:", e)
        }
    }
    
    private fun clearStorage() {
        localStorage.removeItem(USER_KEY)
        localStorage.removeItem(TOKEN_KEY)
    }
}

/**
 * Web User data class
 */
data class WebUser(
    val uid: String,
    val email: String? = null,
    val displayName: String? = null,
    val photoURL: String? = null,
    val phoneNumber: String? = null,
    val isEmailVerified: Boolean = false
)

/**
 * Hook to get auth state
 */
@Composable
fun rememberAuthState(): AuthState {
    val user by AuthManager.currentUser
    val isAuthenticated by AuthManager.isAuthenticated
    val isLoading by AuthManager.isLoading
    
    return AuthState(user, isAuthenticated, isLoading)
}

data class AuthState(
    val user: WebUser?,
    val isAuthenticated: Boolean,
    val isLoading: Boolean
)
